// ABOUTME: Time-based chart showing daily behavior ladder progression with behavior icons at timestamps
// ABOUTME: Displays child's movement through 7-level ladder throughout the day with interactive behavior markers

import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
  ReferenceArea,
} from 'recharts';
import type { DotItemDotProps } from 'recharts';
import {
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Child, BehaviorRecord, BehaviorType } from '../types/api';
import { behaviorAPI, adminAPI } from '../services/api';
import { getBehaviorIcon, KalaIcon } from '../utils/behaviorUtils';
import theme from '../theme/theme';
import { behaviorHeartDark, starAccent } from '../theme/tokens';
import { getTodayInFamilyTimezone, getDateRangeFromTimeWindow, TimeWindowOption } from '../utils/dateUtils';
import { getMobileChartMargins, MOBILE_BREAKPOINT_QUERY } from '../utils/mobileOptimization';
import { computeCompactXDomain } from '../utils/chartTimeWindowUtils';
import {
  getPillTextColor,
  getPillStageName,
  shouldShowHeartBackdrop,
  getHeartEarnedState,
  getPillSide,
  LADDER_POSITION_COLORS as PILL_COLORS,
} from '../utils/avatarPillUtils';

interface AnalyticsTimeChartProps {
  child: Child;
  selectedDate?: string; // Deprecated - use timeWindow instead
  timeWindow?: string | TimeWindowOption; // Unified time window selection
  familyId: number;
  isAdminContext?: boolean; // Optional flag to use admin API instead of family API
  familyTimezone?: string; // Optional family timezone for proper date handling
  isAnonymousAccess?: boolean; // Skip API calls for anonymous access
  behaviorData?: BehaviorRecord[]; // Pre-fetched behavior data for anonymous access
  hideTitle?: boolean; // Hide the component title to prevent redundancy (Issue #826)
  refreshKey?: number; // Increment to force a data re-fetch (e.g., after recording a behavior)
  chartHeight?: number; // Pixel height of the chart area; smaller values give a compact timeline
  // Minimal mode for the All Kids mini-card (issue #1900): hides the ladder-position Y-axis
  // labels (so the plot area runs closer to full width) and narrows the X-axis domain to
  // 6am-10pm by default, expanding only if the day has an event outside that window.
  compact?: boolean;
}

interface BehaviorWithIcon {
  type: BehaviorType;
  notes?: string;
  eventTime?: string;
  icon: React.ReactElement;
  behaviorId: number;
}

interface ChartDataPoint {
  hour: number;
  hourLabel: string;
  ladderPosition: number;
  timestamp: string;
  behaviors: BehaviorWithIcon[];
}

interface BehaviorGroup {
  centerTime: number;
  behaviors: Array<{
    behavior: BehaviorWithIcon;
    time: number;
    ladderPosition: number;
  }>;
}

interface TimelineData {
  dataPoints: ChartDataPoint[];
  avatarPosition: {
    hour: number;
    ladderPosition: number;
    positionType: 'current-time' | 'end-of-day';
    earnedHeart?: boolean; // For completed days, did they earn a heart?
    isFutureDate?: boolean; // Whether this is a future date (no heart backdrop)
  };
  hasBehaviors: boolean;
}

// Some upstream responses (raw API payloads passed through for anonymous access) use
// 'type'/'id' field names instead of the processed 'behaviorType'/'behaviorId' names.
type BehaviorRecordWithRawFields = BehaviorRecord & { type?: BehaviorType; id?: number };

// Shape of a single entry in the admin behavior log API response (BehaviorLogEntry),
// which is looser than BehaviorRecord and needs conversion before use in the chart.
interface AdminBehaviorLogEntry {
  id: number;
  childId: number;
  type: string | number;
  description?: string;
  recordedByUserId?: number;
  loggedBy?: string;
  eventTime?: string;
  createdAt?: string;
  eventTimezone?: string;
  eventLocalTime?: string;
}

// Render props recharts passes to a custom YAxis tick function (Recharts types this as `any`).
interface YAxisTickRenderProps {
  x: number;
  y: number;
  payload: { value: number };
}

// Render props recharts passes to a custom ReferenceDot shape function (Recharts types this as `any`).
interface ReferenceDotShapeRenderProps {
  cx: number;
  cy: number;
}

// Ladder position mapping (matches AnalyticsDashboard human-readable names)
const LADDER_POSITIONS = {
  1: 'Stop &\nReset',
  2: 'Breathe &\nTry Again',
  3: 'Pause',
  4: 'Ready',
  5: 'Good Job',
  6: 'Great Day',
  7: 'Earning\na Heart',
};

// Re-export ladder position colors from shared utility for Y-axis tick coloring
const LADDER_POSITION_COLORS = PILL_COLORS;

// Behavior icon grouping constants
const GROUPING_WINDOW_HOURS = 1/60; // 1 minute grouping window
const ICON_SPACING_PIXELS = 50; // Icon spacing for better visibility (increased from 25)


// Behavior icon mapping (SVG-compatible) - Colors match dashboard theme
const getBehaviorIconSVG = (type: BehaviorType, cx: number, cy: number): React.ReactElement => {
  switch (type) {
    case BehaviorType.Star:
      return (
        <polygon
          points={`${cx},${cy-6} ${cx+2},${cy-2} ${cx+6},${cy-2} ${cx+3},${cy+1} ${cx+4},${cy+5} ${cx},${cy+3} ${cx-4},${cy+5} ${cx-3},${cy+1} ${cx-6},${cy-2} ${cx-2},${cy-2}`}
          fill={theme.palette.success.main}
          stroke={theme.palette.success.dark}
          strokeWidth="0.5"
        />
      );
    case BehaviorType.Triangle:
      return (
        <polygon
          points={`${cx},${cy-5} ${cx+5},${cy+4} ${cx-5},${cy+4}`}
          fill={theme.palette.warning.main}
          stroke={theme.palette.warning.dark}
          strokeWidth="0.5"
        />
      );
    case BehaviorType.Square:
      return (
        <rect
          x={cx-4}
          y={cy-4}
          width={8}
          height={8}
          fill={theme.palette.error.main}
          stroke={theme.palette.error.dark}
          strokeWidth="0.5"
        />
      );
    case BehaviorType.FlipYourCoin:
      return (
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill={theme.palette.info.main}
          stroke={theme.palette.info.dark}
          strokeWidth="1"
        />
      );
    case BehaviorType.MakeItRight:
      return (
        <g>
          <rect
            x={cx-5}
            y={cy-2}
            width={10}
            height={4}
            fill={theme.palette.secondary.main}
            stroke={theme.palette.secondary.dark}
            strokeWidth="0.5"
          />
        </g>
      );
    case BehaviorType.OneOnOneTime:
      return (
        <foreignObject x={cx-8} y={cy-9} width={16} height={16}>
          <KalaIcon sx={{ fontSize: 16 }} />
        </foreignObject>
      );
    case BehaviorType.Reset:
      // Neutral Crossing (Issue #1937) - a calm teal ring, deliberately never a star
      return (
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill="none"
          stroke={theme.palette.crossingAccent}
          strokeWidth="2"
        />
      );
    default:
      return (
        <polygon
          points={`${cx},${cy-6} ${cx+2},${cy-2} ${cx+6},${cy-2} ${cx+3},${cy+1} ${cx+4},${cy+5} ${cx},${cy+3} ${cx-4},${cy+5} ${cx-3},${cy+1} ${cx-6},${cy-2} ${cx-2},${cy-2}`}
          fill={theme.palette.success.main}
          stroke={theme.palette.success.dark}
          strokeWidth="0.5"
        />
      );
  }
};

// Helper function to get time components in family timezone
const getTimeInFamilyTimezone = (date: Date, familyTimezone?: string) => {
  if (familyTimezone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: familyTimezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    const parts = formatter.formatToParts(date);
    const hours = parseInt(parts.find(part => part.type === 'hour')?.value || '0');
    const minutes = parseInt(parts.find(part => part.type === 'minute')?.value || '0');
    const seconds = parseInt(parts.find(part => part.type === 'second')?.value || '0');
    return { hours, minutes, seconds };
  }
  // CRITICAL: When familyTimezone is not provided, we must not fallback to browser timezone
  // This would cause admin users to see times in their timezone instead of family's timezone
  // If no familyTimezone is provided, we should still use UTC as neutral fallback
  return {
    hours: date.getUTCHours(),
    minutes: date.getUTCMinutes(),
    seconds: date.getUTCSeconds()
  };
};

// Helper function to get decimal time in family timezone
const getDecimalTimeInFamilyTimezone = (date: Date, familyTimezone?: string): number => {
  const { hours, minutes, seconds } = getTimeInFamilyTimezone(date, familyTimezone);
  return hours + (minutes / 60) + (seconds / 3600);
};

// Calculate ladder position at a specific decimal time point
const calculateLadderPositionAtTime = (behaviors: BehaviorRecord[], upToTime: number, familyTimezone?: string): number => {
  let position = 4; // Start at Ready
  
  // Sort behaviors by time and filter to only those before the target time
  const relevantBehaviors = behaviors
    .map(behavior => {
      // Use eventTime (canonical timestamp for when behavior actually occurred)
      if (!behavior.eventTime) {
        // Skip behaviors missing eventTime
        return null;
      }
      const timestamp = behavior.eventTime;
      const behaviorTime = new Date(timestamp);
      const decimalTime = getDecimalTimeInFamilyTimezone(behaviorTime, familyTimezone);
      return { ...behavior, decimalTime };
    })
    .filter((behavior): behavior is NonNullable<typeof behavior> => behavior !== null)
    .filter(behavior => behavior.decimalTime <= upToTime)
    .sort((a, b) => a.decimalTime - b.decimalTime);

  relevantBehaviors.forEach(behavior => {
    // Handle both 'type' (from API) and 'behaviorType' (from processed data) field names
    const behaviorType = (behavior as BehaviorRecordWithRawFields).type || behavior.behaviorType;

    switch (behaviorType) {
      case BehaviorType.Star:
        position = Math.min(7, position + 1);
        break;
      case BehaviorType.Triangle:
        position = Math.max(1, position - 1);
        break;
      case BehaviorType.Square:
        position = 1; // Square sends child to bottom - Stop & Reset
        break;
      case BehaviorType.FlipYourCoin:
        // Neutral behavior - does not change ladder position (Issue #679)
        // FlipYourCoin is for emotional regulation recognition, no ladder movement
        break;
      case BehaviorType.MakeItRight:
        // Neutral behavior - does not change ladder position (Issue #679)
        // MakeItRight acknowledges effort but doesn't affect ladder position
        // Note: Square-clearing functionality for heart calculations is handled in backend
        break;
      case BehaviorType.OneOnOneTime:
        // Neutral behavior - does not change ladder position (Issue #1064)
        // OneOnOneTime tracks quality time but doesn't affect ladder position
        break;
      default:
        // Log unhandled behavior types for debugging
        // Skip unknown behavior types
        break;
    }
  });

  return position;
};

const AnalyticsTimeChart: React.FC<AnalyticsTimeChartProps> = ({
  child,
  selectedDate,
  timeWindow,
  familyId,
  isAdminContext = false,
  familyTimezone,
  isAnonymousAccess = false,
  behaviorData = [],
  hideTitle = false,
  refreshKey,
  chartHeight = 390,
  compact = false,
}) => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT_QUERY);
  const chartMargins = getMobileChartMargins('timeChart', isMobile);
  // A small left margin so the "6am" tick label (centered on the domain's first tick,
  // right at the plot's left edge) isn't clipped — 0 left margin cut it off (issue #1900)
  const compactChartMargins = { ...chartMargins, left: 15, right: 2 };

  // Determine the actual date to use - prioritize timeWindow over selectedDate
  const actualDate = React.useMemo(() => {
    if (timeWindow) {
      const dateRange = getDateRangeFromTimeWindow(timeWindow, familyTimezone);
      // For time chart, use the end date (most recent date in range)
      return dateRange.endDate;
    }
    return selectedDate || getTodayInFamilyTimezone(familyTimezone);
  }, [timeWindow, selectedDate, familyTimezone]);
  const [timelineData, setTimelineData] = useState<TimelineData | null>(null);
  const [loading, setLoading] = useState(true);

  // Memoize behaviorData to prevent infinite loops when it's passed as prop
  // Use empty array as stable reference when no behaviorData is provided
  const emptyBehaviorData = React.useMemo(() => [], []);
  const stableBehaviorData = React.useMemo(() => {
    return behaviorData || emptyBehaviorData;
  }, [behaviorData, emptyBehaviorData]);

  // Stabilize child dependency to prevent infinite loops when child object reference changes
  const stableChildId = React.useMemo(() => {
    return child.childId || child.id;
  }, [child.childId, child.id]);

  // Helper function to check if date is "today" in family timezone
  const isTodayInFamilyTimezone = (dateString: string): boolean => {
    const today = getTodayInFamilyTimezone(familyTimezone);
    return dateString === today;
  };

  // Helper function to determine if date is in the future
  const isFutureDateInFamilyTimezone = (dateString: string): boolean => {
    const today = getTodayInFamilyTimezone(familyTimezone);
    return dateString > today;
  };

  useEffect(() => {
    const fetchBehaviorData = async () => {
      setLoading(true);
      
      // Use pre-fetched data for anonymous access
      if (isAnonymousAccess) {
        const behaviorRecords = stableBehaviorData;

        // Process the behavior data using the same logic as authenticated access
        const dataPoints: ChartDataPoint[] = [];
        const isToday = isTodayInFamilyTimezone(actualDate);
        const isFutureDate = isFutureDateInFamilyTimezone(actualDate);

        // Create data points for each behavior change + hourly markers
        const timePoints = new Set<number>();
        
        // Add behavior times
        behaviorRecords.forEach(behavior => {
          // Use eventTime (canonical timestamp for when behavior actually occurred)
          const timestamp = behavior.eventTime;
          if (timestamp) {
            const behaviorTime = new Date(timestamp);
            const decimalTime = getDecimalTimeInFamilyTimezone(behaviorTime, familyTimezone);
            timePoints.add(decimalTime);
          }
        });

        // Add hourly markers
        for (let hour = 0; hour <= 23; hour++) {
          timePoints.add(hour);
        }

        // If today, add current time
        let currentDecimalTime = 12; // Default fallback
        if (isToday) {
          currentDecimalTime = getDecimalTimeInFamilyTimezone(new Date(), familyTimezone);
          timePoints.add(currentDecimalTime);
        }

        // Determine cutoff time - for today stop at current time, for future dates show no timeline, for past dates show full day
        const cutoffTime = isToday ? currentDecimalTime : isFutureDate ? 0 : 24;

        // Create data points
        Array.from(timePoints).sort((a, b) => a - b).forEach(timePoint => {
          // Only include data points up to the cutoff time
          if (timePoint > cutoffTime) return;
          const hour = Math.floor(timePoint);
          const minutes = Math.floor((timePoint - hour) * 60);
          let hourLabel = hour === 0 ? '12am' : hour <= 12 ? `${hour === 12 ? 12 : hour}${hour < 12 ? 'am' : 'pm'}` : `${hour - 12}pm`;
          if (hour === 24) hourLabel = '12am';

          // Get behaviors at this time point
          const behaviorWithIcons = behaviorRecords
            .filter(behavior => {
              if (!behavior.eventTime) return false;
              const timestamp = behavior.eventTime;
              const behaviorTime = new Date(timestamp);
              const decimalTime = getDecimalTimeInFamilyTimezone(behaviorTime, familyTimezone);
              return Math.abs(decimalTime - timePoint) < 0.01; // Within ~30 seconds
            })
            .map(behavior => {
              // API returns 'type' as integer, convert to BehaviorType enum
              const behaviorType = (behavior as BehaviorRecordWithRawFields).type || behavior.behaviorType;
              const icon = getBehaviorIcon(behaviorType, { sx: { fontSize: 16 } });

              return {
                type: behaviorType,
                notes: behavior.notes,
                eventTime: behavior.eventTime || '',
                icon,
                behaviorId: (behavior as BehaviorRecordWithRawFields).id || behavior.behaviorId
              };
            });

          // Calculate ladder position up to this time point
          const ladderPosition = calculateLadderPositionAtTime(behaviorRecords, timePoint, familyTimezone);

          dataPoints.push({
            hour: timePoint,
            hourLabel: minutes > 0 ? `${hourLabel} +${minutes}m` : hourLabel,
            ladderPosition,
            timestamp: `${actualDate}T${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00Z`,
            behaviors: behaviorWithIcons,
          });
        });

        // Add final point
        if (isToday) {
          if (dataPoints.length === 0 || dataPoints[dataPoints.length - 1].hour < currentDecimalTime) {
            const currentLadderPosition = calculateLadderPositionAtTime(behaviorRecords, currentDecimalTime, familyTimezone);
            dataPoints.push({
              hour: currentDecimalTime,
              hourLabel: `Current`,
              ladderPosition: currentLadderPosition,
              timestamp: new Date().toISOString(),
              behaviors: [],
            });
          }
        } else {
          if (dataPoints.length === 0 || dataPoints[dataPoints.length - 1].hour < 24) {
            const endOfDayPosition = calculateLadderPositionAtTime(behaviorRecords, 24, familyTimezone);
            dataPoints.push({
              hour: 24,
              hourLabel: `12am`,
              ladderPosition: endOfDayPosition,
              timestamp: `${actualDate}T23:59:59Z`,
              behaviors: [],
            });
          }
        }

        // Calculate final position and heart earned
        const finalLadderPosition = dataPoints.length > 0 ? dataPoints[dataPoints.length - 1].ladderPosition : 4;
        const earnedHeart = finalLadderPosition >= 6; // Heart level or above
        const avatarTime = isToday ? currentDecimalTime : isFutureDate ? 4 : 24; // Future dates show starting position

        const avatarPosition = {
          hour: avatarTime,
          ladderPosition: isFutureDate ? 4 : finalLadderPosition, // Future dates start at Ready position
          positionType: isToday ? 'current-time' as const : 'end-of-day' as const,
          earnedHeart: !isToday && !isFutureDate ? earnedHeart : undefined, // No heart status for future dates
          isFutureDate,
        };

        setTimelineData({
          dataPoints,
          avatarPosition,
          hasBehaviors: behaviorRecords.length > 0
        });
        setLoading(false);
        return;
      }
      
      try {
        let behaviorRecords: BehaviorRecord[] = [];
        
        if (isAdminContext) {
          // Use admin API which has working data
          const childId = stableChildId;
          if (!childId) throw new Error('Child ID is required for admin API');
          
          const adminResponse = await adminAPI.getChildBehaviorLog(childId.toString(), {
            page: 1,
            pageSize: 100, // Reasonable page size with server-side date filtering
            sortBy: 'eventTime',
            sortOrder: 'asc',
            startDate: actualDate,
            endDate: actualDate
          });

          // Convert admin API response to BehaviorRecord format (server-side date filtering applied)
          const allBehaviors: AdminBehaviorLogEntry[] = adminResponse.events || [];

          // Map admin API BehaviorLogEntry to BehaviorRecord format
          behaviorRecords = allBehaviors
            .map((behavior: AdminBehaviorLogEntry): BehaviorRecord => {
              // Convert behavior type string to BehaviorType enum
              let behaviorType: BehaviorType;
              const rawType = behavior.type;
              
              if (typeof rawType === 'number') {
                behaviorType = rawType;
              } else if (typeof rawType === 'string') {
                switch (rawType.toLowerCase()) {
                  case 'star': 
                  case '1': 
                    behaviorType = BehaviorType.Star; 
                    break;
                  case 'triangle': 
                  case '2': 
                    behaviorType = BehaviorType.Triangle; 
                    break;
                  case 'square': 
                  case '3': 
                    behaviorType = BehaviorType.Square; 
                    break;
                  case 'flipyourcoin': 
                  case '4': 
                    behaviorType = BehaviorType.FlipYourCoin; 
                    break;
                  case 'makeitright':
                  case '5':
                    behaviorType = BehaviorType.MakeItRight;
                    break;
                  case 'oneoneonetime':
                  case '1:1 time':
                  case '6':
                    behaviorType = BehaviorType.OneOnOneTime;
                    break;
                  case 'reset':
                  case '8':
                    behaviorType = BehaviorType.Reset;
                    break;
                  default:
                    behaviorType = BehaviorType.Star;
                    break;
                }
              } else {
                behaviorType = BehaviorType.Star;
              }
              
              // Map all fields correctly: BehaviorLogEntry → BehaviorRecord
              return {
                behaviorId: behavior.id,
                childId: behavior.childId,
                behaviorType: behaviorType,
                notes: behavior.description,
                recordedByUserId: behavior.recordedByUserId || 0,
                recordedByUserName: behavior.loggedBy,
                eventTime: behavior.eventTime || behavior.createdAt,
                eventTimezone: behavior.eventTimezone,
                eventLocalTime: behavior.eventLocalTime,
              } as BehaviorRecord;
            })
            .filter((behavior) => {
              // Basic validation since server handles date filtering
              return behavior.eventTime && !isNaN(new Date(behavior.eventTime).getTime());
            });
        } else {
          // Use child-specific API for child dashboard
          const childId = stableChildId;
          if (!childId) throw new Error('Child ID is required');
          
          // Call the child-specific behavior endpoint with date parameter
          behaviorRecords = await behaviorAPI.getChildBehaviorsByDate(childId, actualDate, familyTimezone);
        }
        
        const dataPoints: ChartDataPoint[] = [];
        const isToday = isTodayInFamilyTimezone(actualDate);
        const isFutureDate = isFutureDateInFamilyTimezone(actualDate);

        // Create data points for each behavior change + hourly markers
        const timePoints = new Set<number>();
        
        // Add hourly markers
        for (let hour = 0; hour <= 23; hour++) {
          timePoints.add(hour);
        }
        
        // Add behavior timestamps using family timezone
        behaviorRecords.forEach(behavior => {
          // Use eventTime (canonical timestamp for when behavior actually occurred)
          if (!behavior.eventTime) {
            // Skip behaviors missing eventTime
            return;
          }
          const timestamp = behavior.eventTime;
          const behaviorTime = new Date(timestamp);
          const decimalHour = getDecimalTimeInFamilyTimezone(behaviorTime, familyTimezone);
          timePoints.add(decimalHour);
        });

        // Determine cutoff time - for today stop at current time, for future dates show no timeline, for past dates show full day
        const now = new Date();
        const currentDecimalTime = getDecimalTimeInFamilyTimezone(now, familyTimezone);
        const cutoffTime = isToday ? currentDecimalTime : isFutureDate ? 0 : 24;

        // Sort time points and create chart data
        const sortedTimePoints = Array.from(timePoints).sort((a, b) => a - b);
        
        sortedTimePoints.forEach(timePoint => {
          // Only include data points up to the cutoff time
          if (timePoint > cutoffTime) return;
          
          const hour = Math.floor(timePoint);
          const minutes = Math.round((timePoint % 1) * 60);
          
          const hourLabel = hour === 0 ? '12am' : 
                           hour < 12 ? `${hour}am` :
                           hour === 12 ? '12pm' :
                           `${hour - 12}pm`;

          // Get behaviors that occur exactly at this time point using family timezone
          const pointBehaviors = behaviorRecords.filter(behavior => {
            // Use eventTime (canonical timestamp for when behavior actually occurred)
            if (!behavior.eventTime) return false;
            const timestamp = behavior.eventTime;
            const behaviorTime = new Date(timestamp);
            const behaviorDecimalHour = getDecimalTimeInFamilyTimezone(behaviorTime, familyTimezone);
            return Math.abs(behaviorDecimalHour - timePoint) < 0.001; // Very close match
          });

          const behaviorWithIcons = pointBehaviors
            .filter(behavior => behavior.eventTime) // Only include behaviors with eventTime
            .map(behavior => ({
              type: behavior.behaviorType,
              notes: behavior.notes,
              eventTime: behavior.eventTime!, // Use eventTime (when behavior actually occurred), non-null after filter
              icon: getBehaviorIcon(behavior.behaviorType, { sx: { fontSize: 16 } }),
              behaviorId: behavior.behaviorId || 0,
            }));

          // Calculate ladder position up to this time point using family timezone
          const ladderPosition = calculateLadderPositionAtTime(behaviorRecords, timePoint, familyTimezone);

          dataPoints.push({
            hour: timePoint, // Use decimal hour for precise positioning
            hourLabel: minutes > 0 ? `${hourLabel} +${minutes}m` : hourLabel,
            ladderPosition,
            timestamp: `${actualDate}T${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00Z`,
            behaviors: behaviorWithIcons,
          });
        });

        // Add a final point to ensure the line extends to the end
        if (isToday) {
          // For today, extend to current time
          if (dataPoints.length === 0 || dataPoints[dataPoints.length - 1].hour < currentDecimalTime) {
            const currentLadderPosition = calculateLadderPositionAtTime(behaviorRecords, currentDecimalTime, familyTimezone);
            dataPoints.push({
              hour: currentDecimalTime,
              hourLabel: `Current`,
              ladderPosition: currentLadderPosition,
              timestamp: new Date().toISOString(),
              behaviors: [],
            });
          }
        } else {
          // For past days, extend to midnight (24)
          if (dataPoints.length === 0 || dataPoints[dataPoints.length - 1].hour < 24) {
            const endOfDayPosition = calculateLadderPositionAtTime(behaviorRecords, 24, familyTimezone);
            dataPoints.push({
              hour: 24,
              hourLabel: `12am`,
              ladderPosition: endOfDayPosition,
              timestamp: `${actualDate}T23:59:59Z`,
              behaviors: [],
            });
          }
        }

        // Determine avatar position - show at current time for today, no avatar for future dates, end of day for past dates
        const avatarTime = isToday ? currentDecimalTime : isFutureDate ? 4 : 24; // Future dates show at start of day
        const finalLadderPosition = calculateLadderPositionAtTime(behaviorRecords, isToday ? avatarTime : isFutureDate ? 0 : 24, familyTimezone);
        
        // Get proper heart status from backend DailyProgress API (don't calculate client-side)
        let earnedHeart: boolean | undefined = undefined;
        if (!isToday && !isFutureDate) {
          try {
            const childId = stableChildId;
            const dailyProgress = await behaviorAPI.getDailyProgress(childId!, actualDate);
            earnedHeart = dailyProgress.earnedHeart;
          } catch (error) {
            // Handle 404 or other API errors gracefully - don't break the timeline
            console.warn('Daily progress API unavailable for heart calculation. Error:', error);
            
            // Fallback to simple client-side calculation for now
            // This is temporary until the API endpoint issue is resolved
            const endOfDayPosition = calculateLadderPositionAtTime(behaviorRecords, 24, familyTimezone);
            earnedHeart = endOfDayPosition >= 7;
          }
        }
        
        const avatarPosition = {
          hour: avatarTime,
          ladderPosition: isFutureDate ? 4 : finalLadderPosition, // Future dates start at Ready position
          positionType: isToday ? 'current-time' as const : 'end-of-day' as const,
          earnedHeart: !isToday && !isFutureDate ? earnedHeart : undefined, // No heart status for future dates
          isFutureDate,
        };

        setTimelineData({
          dataPoints,
          avatarPosition,
          hasBehaviors: behaviorRecords.length > 0
        });
      } catch (error) {
        console.error('Failed to fetch behavior data:', error);
        setTimelineData({ 
          dataPoints: [], 
          avatarPosition: { hour: 0, ladderPosition: 4, positionType: 'current-time', isFutureDate: false },
          hasBehaviors: false
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBehaviorData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableChildId, actualDate, familyId, isAdminContext, familyTimezone, isAnonymousAccess, refreshKey]);

  // Short date format for the title: "Fri, Mar 6"
  const formatShortDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    const tz = familyTimezone || 'UTC';
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short', timeZone: tz });
    const monthName = date.toLocaleDateString('en-US', { month: 'short', timeZone: tz });
    return `${weekday}, ${monthName} ${day}`;
  };

  const getBehaviorTypeName = (type: BehaviorType): string => {
    switch (type) {
      case BehaviorType.Star: return 'Star';
      case BehaviorType.Triangle: return 'Triangle';
      case BehaviorType.Square: return 'Storm';
      case BehaviorType.FlipYourCoin: return 'Reset';
      case BehaviorType.MakeItRight: return 'MakeItRight';
      case BehaviorType.OneOnOneTime: return 'Let Go';
      default: return 'Unknown';
    }
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const options: Intl.DateTimeFormatOptions = { 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true 
    };
    
    // CRITICAL: Always display in family timezone, never admin's browser timezone
    if (familyTimezone) {
      options.timeZone = familyTimezone;
    } else {
      // If no family timezone provided, use UTC as neutral fallback
      // This prevents admin browser timezone from affecting display
      options.timeZone = 'UTC';
    }
    
    return date.toLocaleTimeString('en-US', options);
  };



  // Only show the full loading placeholder on the very first load (no data yet).
  // On background refreshes (e.g. SignalR-triggered), keep the existing chart visible
  // to avoid a layout/size flicker while new data is fetched.
  if (loading && timelineData === null) {
    return (
      <Box
        data-testid="chart-loading"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: chartHeight + 10
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!timelineData) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No behaviors recorded this day
        </Typography>
      </Box>
    );
  }

  // Compact mode narrows the day to 6am-10pm by default, widening only far enough to
  // include any event outside that window (issue #1900). Full mode always shows the whole day.
  // dataPoints includes one baseline entry per hour of the day (0-23), not just hours
  // with an actual behavior — filter to real events only, or every compact chart would
  // see an hour-0 point and widen the domain back to midnight (issue #1900).
  const xDomain: [number, number] = compact
    ? computeCompactXDomain(
        timelineData.dataPoints.filter(dp => dp.behaviors.length > 0).map(dp => dp.hour)
      )
    : [0, 24];

  // The chart's own clip-path is disabled (see the heart-backdrop/pill comment below),
  // so points outside the axis domain won't be clipped by recharts — they'd still render,
  // extrapolated past the visible plot area. Filter the actual dataset to the domain in
  // compact mode rather than relying on axis clipping (issue #1900).
  const chartDataPoints = compact
    ? timelineData.dataPoints.filter(dp => dp.hour >= xDomain[0] && dp.hour <= xDomain[1])
    : timelineData.dataPoints;

  return (
    <Box
      data-testid="analytics-time-chart"
      sx={{
        width: '100%',
        position: 'relative',
        // Allow heart backdrop and stage pill to render outside chart clip area
        '& .recharts-surface': { overflow: 'visible' },
        '& svg': { overflow: 'visible' },
        // Recharts wraps chart content in a <g clip-path="url(#...)"> that clips
        // the heart backdrop and pill. Disable it — data is bounded by axis domains.
        '& .recharts-surface g[clip-path]': { clipPath: 'none !important' },
      }}
    >
      {!hideTitle && (
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
          {child?.name || 'Child'}&rsquo;s Day &mdash; {formatShortDate(actualDate)}
        </Typography>
      )}

      {/* Empty state is visually evident from the chart itself */}

      <ResponsiveContainer width="100%" height={chartHeight}>
        <LineChart
          data={chartDataPoints}
          margin={compact ? compactChartMargins : chartMargins}
        >
          {/* Full mode: default grid. Compact mode (issue #1900): no vertical lines at all, and
              horizontal lines only at the interior ladder positions (2-6) — the domain's own
              top/bottom (1 and 7) are skipped since a line exactly on the plot's edge reads as
              a border/frame rather than a reference line. */}
          {compact
            ? <CartesianGrid strokeDasharray="3 3" vertical={false} horizontalValues={[2, 3, 4, 5, 6]} />
            : <CartesianGrid strokeDasharray="3 3" />
          }
          <XAxis
            dataKey="hour"
            type="number"
            scale="linear"
            domain={xDomain}
            axisLine={!compact}
            tickLine={!compact}
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => {
              const hour = Math.floor(value);
              return hour === 0 ? '12am' :
                     hour < 12 ? `${hour}am` :
                     hour === 12 ? '12pm' :
                     hour === 24 ? '12am' :
                     `${hour - 12}pm`;
            }}
            ticks={compact ? [6, 12, 17, 22] : [0, 3, 6, 9, 12, 15, 18, 21, 24]}
          />
          <YAxis
            hide={compact}
            domain={[1, 7]}
            ticks={[1, 2, 3, 4, 5, 6, 7]}
            interval={0}
            tickFormatter={(value) => LADDER_POSITIONS[value as keyof typeof LADDER_POSITIONS] || value}
            tick={(props: YAxisTickRenderProps) => {
              const { x, y, payload } = props;
              const position = payload.value;
              const color = LADDER_POSITION_COLORS[position as keyof typeof LADDER_POSITION_COLORS] || theme.palette.text.secondary;
              const labelText = LADDER_POSITIONS[position as keyof typeof LADDER_POSITIONS] || position;

              // Handle line breaks for wrapped labels
              if (typeof labelText === 'string' && labelText.includes('\n')) {
                const lines = labelText.split('\n');
                const lineHeight = 12;
                const startY = y - ((lines.length - 1) * lineHeight) / 2;

                return (
                  <g>
                    {lines.map((line, index) => (
                      <text
                        key={index}
                        x={x}
                        y={startY + (index * lineHeight)}
                        textAnchor="end"
                        dominantBaseline="middle"
                        fontSize="10"
                        fill={color}
                        fontWeight="bold"
                      >
                        {line}
                      </text>
                    ))}
                  </g>
                );
              }

              // Single line labels (no change)
              return (
                <text
                  x={x}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize="10"
                  fill={color}
                  fontWeight="bold"
                >
                  {labelText}
                </text>
              );
            }}
            width={compact ? 0 : 80}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length > 0 && label !== undefined) {
                // Find all behaviors near this time point - but ensure uniqueness
                const nearbyBehaviorsMap = new Map<number, BehaviorWithIcon>();
                const iconWidthInHours = GROUPING_WINDOW_HOURS;
                const labelNumber = typeof label === 'number' ? label : parseFloat(String(label));
                
                timelineData?.dataPoints.forEach(point => {
                  if (Math.abs(point.hour - labelNumber) < iconWidthInHours) {
                    point.behaviors.forEach(behavior => {
                      // Only add each behavior once by behaviorId
                      if (!nearbyBehaviorsMap.has(behavior.behaviorId)) {
                        nearbyBehaviorsMap.set(behavior.behaviorId, behavior);
                      }
                    });
                  }
                });
                
                const nearbyBehaviors = Array.from(nearbyBehaviorsMap.values());
                
                if (nearbyBehaviors.length > 0) {
                  // Sort by time
                  nearbyBehaviors.sort((a, b) => {
                    const timeA = new Date(a.eventTime || '').getTime();
                    const timeB = new Date(b.eventTime || '').getTime();
                    return timeA - timeB;
                  });
                  
                  if (nearbyBehaviors.length === 1) {
                    const behavior = nearbyBehaviors[0];
                    return (
                      <Box
                        sx={{
                          backgroundColor: 'white',
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          padding: 1,
                          boxShadow: 2,
                          // Tooltip container fontSize; children are Typography with their own explicit variant, so no variant swap applies here
                          fontSize: '0.875rem',
                          minWidth: 200,
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {getBehaviorTypeName(behavior.type)}
                        </Typography>
                        <Typography variant="body2">
                          {formatTime(behavior.eventTime || '')}
                        </Typography>
                        {behavior.notes && (
                          <Typography variant="body2" color="text.secondary">
                            {behavior.notes}
                          </Typography>
                        )}
                      </Box>
                    );
                  } else {
                    // Multiple behaviors - show all in tooltip
                    const firstTime = formatTime(nearbyBehaviors[0].eventTime || '');
                    return (
                      <Box
                        data-testid="grouped-behaviors-tooltip"
                        sx={{
                          backgroundColor: 'white',
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          padding: 1,
                          boxShadow: 2,
                          // Tooltip container fontSize; children are Typography with their own explicit variant, so no variant swap applies here
                          fontSize: '0.875rem',
                          minWidth: 250,
                          maxWidth: 350,
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                          {nearbyBehaviors.length} behaviors at {firstTime.replace(/:00/, '')}
                        </Typography>
                        {nearbyBehaviors.map((behavior, idx) => (
                          <Box key={idx} sx={{ mb: idx < nearbyBehaviors.length - 1 ? 0.5 : 0 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {getBehaviorTypeName(behavior.type)}
                              {behavior.notes && ` - ${behavior.notes}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(behavior.eventTime || '')}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    );
                  }
                }
              }
              return null;
            }}
          />
          {/* Three background zones - red/yellow/green at 1/3s matching PositiveNegativeBehaviorsChart —
              omitted in compact mode (issue #1900), which matches the mockup's flat, unzoned band */}
          {!compact && (
          <>
          <ReferenceArea
            y1={5}
            y2={7}
            fill={alpha(theme.palette.success.main, 0.2)}
            data-testid="timeline-green-zone"
          />
          <ReferenceArea
            y1={3}
            y2={5}
            fill={alpha(starAccent, 0.2)}
            data-testid="timeline-yellow-zone"
          />
          <ReferenceArea
            y1={1}
            y2={3}
            fill={alpha(theme.palette.error.main, 0.2)}
            data-testid="timeline-red-zone"
          />
          </>
          )}
          <Line
            type={compact ? 'stepAfter' : 'monotone'}
            dataKey="ladderPosition"
            stroke={compact ? theme.palette.success.dark : theme.palette.info.main}
            strokeWidth={3}
            dot={(props: DotItemDotProps) => {
              const { cx, cy, payload } = props;

              // Only show white hour markers for exact hours without behaviors
              const isExactHour = payload && payload.hour && Math.abs(payload.hour - Math.round(payload.hour)) < 0.001;
              const hasBehaviors = payload && payload.behaviors && payload.behaviors.length > 0;
              
              if (isExactHour && !hasBehaviors) {
                return (
                  <circle
                    key={`hour-${cx}-${cy}-${payload.hour}`}
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill="white"
                    stroke={theme.palette.grey[300]}
                    strokeWidth={1}
                  />
                );
              }
              
              // For all other points, return invisible dot
              return (
                <circle
                  key={`empty-${cx}-${cy}-${payload.hour}`}
                  cx={cx}
                  cy={cy}
                  r={1}
                  fill="transparent"
                  stroke="transparent"
                />
              );
            }}
            connectNulls={true}
            data-testid="ladder-line"
            data-start-position="4"
            data-position-changes={timelineData.dataPoints
              .filter(point => point.behaviors.length > 0)
              .map(point => `${point.hour}:${new Date(point.timestamp).getMinutes()}`)
              .join(',')}
            data-flat-position={timelineData.dataPoints.every(point => point.ladderPosition === 4) ? "4" : undefined}
          />
          
          {/* Custom behavior rendering with grouping logic */}
          {(() => {
            // Extract all unique behaviors from data points
            const allBehaviorsMap = new Map<number, {
              behavior: BehaviorWithIcon;
              time: number;
              ladderPosition: number;
            }>();
            
            timelineData.dataPoints
              .filter(point => point.behaviors.length > 0)
              .forEach(point => {
                point.behaviors.forEach(behavior => {
                  // Only add each behavior once, even if it appears in multiple data points
                  if (!allBehaviorsMap.has(behavior.behaviorId)) {
                    allBehaviorsMap.set(behavior.behaviorId, {
                      behavior,
                      time: point.hour,
                      ladderPosition: point.ladderPosition,
                    });
                  }
                });
              });
            
            const allBehaviors = Array.from(allBehaviorsMap.values());
            
            // Group behaviors that would visually overlap
            const groups: BehaviorGroup[] = [];
            const processedBehaviors = new Set<number>();
            const iconWidthInHours = GROUPING_WINDOW_HOURS;
            
            allBehaviors.forEach(behaviorItem => {
              if (processedBehaviors.has(behaviorItem.behavior.behaviorId)) return;
              
              // Find all behaviors that would overlap with this one
              const overlappingBehaviors: typeof groups[0]['behaviors'] = [behaviorItem];
              processedBehaviors.add(behaviorItem.behavior.behaviorId);
              
              // Check all other behaviors for visual overlap
              allBehaviors.forEach(otherBehaviorItem => {
                if (processedBehaviors.has(otherBehaviorItem.behavior.behaviorId)) return;
                
                // Check if behaviors would visually overlap
                const timeDiff = Math.abs(otherBehaviorItem.time - behaviorItem.time);
                if (timeDiff < iconWidthInHours) {
                  overlappingBehaviors.push(otherBehaviorItem);
                  processedBehaviors.add(otherBehaviorItem.behavior.behaviorId);
                }
              });
              
              if (overlappingBehaviors.length > 0) {
                // Sort by time for left-to-right chronological order
                overlappingBehaviors.sort((a, b) => {
                  const timeA = new Date(a.behavior.eventTime || '').getTime();
                  const timeB = new Date(b.behavior.eventTime || '').getTime();
                  return timeA - timeB;
                });
                
                // Calculate center time as average
                const centerTime = overlappingBehaviors.reduce((sum, b) => sum + b.time, 0) / overlappingBehaviors.length;
                
                groups.push({
                  centerTime,
                  behaviors: overlappingBehaviors,
                });
              }
            });
            
            return groups.map((group, groupIndex) => {
              const behaviorCount = group.behaviors.length;
              const iconSpacing = ICON_SPACING_PIXELS;
              
              return group.behaviors.map((item, index) => {
                // Calculate x offset for this behavior in the group
                const xOffsetInHours = (index - (behaviorCount - 1) / 2) * (iconSpacing / 100); // Convert pixels to hours - increased from 300 to 100 for better visibility
                
                return (
                  <ReferenceDot
                    key={`behavior-group-${groupIndex}-${item.behavior.behaviorId}`}
                    x={group.centerTime + xOffsetInHours}
                    y={item.ladderPosition}
                    r={12}
                    fill="white"
                    stroke={compact ? theme.palette.success.dark : theme.palette.info.main}
                    strokeWidth={2}
                    shape={(props: ReferenceDotShapeRenderProps) => {
                      const { cx, cy } = props;
                      return (
                        // Recharts primitive/render-prop internals — raw SVG, sx doesn't apply
                        <g
                          data-testid={`behavior-icon-${item.behavior.behaviorId}`}
                          data-behavior-type={getBehaviorTypeName(item.behavior.type)}
                          style={{ cursor: 'pointer' }}
                        >
                          <circle
                            cx={cx}
                            cy={cy}
                            r={12}
                            fill="white"
                            stroke={compact ? theme.palette.success.dark : theme.palette.info.main}
                            strokeWidth={2}
                          />
                          <g transform={`translate(${cx}, ${cy})`}>
                            {getBehaviorIconSVG(item.behavior.type, 0, 0)}
                          </g>
                        </g>
                      );
                    }}
                  />
                );
              });
            });
          })()}
          
          {/* Child avatar with stage pill and heart backdrop — omitted in compact mode
              (issue #1900), which shows only the event timeline itself, no avatar marker */}
          {!compact && (
          <ReferenceDot
            key="child-avatar-on-line"
            x={timelineData.avatarPosition.hour}
            y={timelineData.avatarPosition.ladderPosition}
            r={16}
            fill="white"
            stroke={theme.palette.info.main}
            strokeWidth={3}
            shape={(props: ReferenceDotShapeRenderProps) => {
              const { cx, cy } = props;
              const { positionType, ladderPosition, earnedHeart, isFutureDate: futureDate } = timelineData.avatarPosition;
              const isCompletedDay = positionType === 'end-of-day' && !futureDate;

              // Heart backdrop state
              const showHeart = shouldShowHeartBackdrop(positionType, ladderPosition, !!futureDate);
              const heartEarned = showHeart ? getHeartEarnedState(positionType, ladderPosition, earnedHeart) : false;

              // Stage pill state
              const stageName = getPillStageName(ladderPosition);
              const pillBgColor = PILL_COLORS[ladderPosition] || theme.palette.grey[500];
              const pillTextColor = getPillTextColor(ladderPosition);
              const pillSide = getPillSide(timelineData.avatarPosition.hour);
              const isMultiLine = stageName.includes('\n');
              const fontSize = isMobile ? 8 : 10;
              const pillHeight = isMultiLine ? 28 : 18;
              const pillWidth = isMobile ? (isMultiLine ? 65 : 55) : (isMultiLine ? 80 : 70);
              // Adjust pill width for longer stage names
              const adjustedPillWidth = stageName.replace('\n', '').length > 10 ? pillWidth + (isMobile ? 15 : 20) : pillWidth;

              // Pill positioning
              const avatarRadius = 16;
              const pillGap = 2;
              const pillX = pillSide === 'right'
                ? cx + avatarRadius + pillGap
                : cx - avatarRadius - pillGap - adjustedPillWidth;
              const pillTextX = pillX + adjustedPillWidth / 2;

              // Circle border color — pink when heart earned, red when not, blue for today
              const circleStroke = isCompletedDay
                ? (heartEarned ? theme.palette.behaviorHeart : theme.palette.error.main)
                : (showHeart && heartEarned ? theme.palette.behaviorHeart : theme.palette.info.main);

              // Accessibility label
              const ariaLabel = `${child?.name || 'Child'} at ${stageName.replace('\n', ' ')}${showHeart ? (heartEarned ? ', heart earned' : ', heart not earned') : ''}`;

              return (
                <g data-testid="avatar-group" role="img" aria-label={ariaLabel}>
                  {/* 1. Heart backdrop (behind avatar) — uses MUI FavoriteOutlined path */}
                  {showHeart && (
                    <g transform={`translate(${cx - 30}, ${cy - 28}) scale(2.5)`} data-testid="heart-backdrop">
                      <path
                        d="m12 21.35-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54z"
                        fill={heartEarned ? theme.palette.behaviorHeart : 'none'}
                        stroke={heartEarned ? behaviorHeartDark : theme.palette.grey[500]}
                        strokeWidth={heartEarned ? 0.3 : 0.8}
                        opacity={0.7}
                      />
                    </g>
                  )}

                  {/* 2. Avatar circle — always shows profile image or initials */}
                  <circle
                    cx={cx}
                    cy={cy}
                    r={avatarRadius}
                    fill="white"
                    stroke={circleStroke}
                    strokeWidth={3}
                  />
                  {child.profileImageUrl ? (
                    <image
                      x={cx - 14}
                      y={cy - 14}
                      width={28}
                      height={28}
                      href={child.profileImageUrl}
                      clipPath="circle(14px at 14px 14px)"
                    />
                  ) : (
                    <text
                      x={cx}
                      y={cy}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize="14"
                      fontWeight="bold"
                      fill={circleStroke}
                    >
                      {child?.name?.charAt(0)?.toUpperCase() || 'C'}
                    </text>
                  )}

                  {/* 3. Stage pill */}
                  <rect
                    x={pillX}
                    y={cy - pillHeight / 2}
                    width={adjustedPillWidth}
                    height={pillHeight}
                    rx={9}
                    ry={9}
                    fill={pillBgColor}
                    data-testid="stage-pill"
                  />
                  {isMultiLine ? (
                    // Two-line pill text (stage 2)
                    stageName.split('\n').map((line, i) => (
                      <text
                        key={i}
                        x={pillTextX}
                        y={cy + (i === 0 ? -5 : 7)}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={fontSize}
                        fontWeight="bold"
                        fill={pillTextColor}
                      >
                        {line}
                      </text>
                    ))
                  ) : (
                    <text
                      x={pillTextX}
                      y={cy}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={fontSize}
                      fontWeight="bold"
                      fill={pillTextColor}
                    >
                      {stageName}
                    </text>
                  )}
                </g>
              );
            }}
            data-testid="child-avatar"
            data-position-type={timelineData.avatarPosition.positionType}
          />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default React.memo(AnalyticsTimeChart);